import DataOps from './DataOps';
import EmailService from './EmailService';

class NotificationOps {
    /**
     * Create a notification in a user's personal subcollection
     * Path: users/{userId}/notifications/{notificationId}
     * This scales to millions of users without performance issues
     */
    static async createNotification(
        userId: string, 
        message: string, 
        type: string = 'general',
        sessionId?: string,
        campId?: string,
        metadata?: any
    ) {
        try {
            const notificationId = `notification_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
            const notification: any = {
                id: notificationId,
                message,
                type,
                read: false,
                createdAt: new Date(),
                ...metadata // Allow flexible metadata (targetUserId, commentId, etc.)
            };

            // Only add sessionId and campId if they are defined (Firestore doesn't allow undefined values)
            if (sessionId) notification.sessionId = sessionId;
            if (campId) notification.campId = campId;

            // Default: save to user collection (family accounts)
            await DataOps.saveToSubcollection('user', userId, 'notifications', notificationId, notification);
            
            return notification;
        } catch (error) {
            console.error(`Error creating notification for user ${userId}:`, error);
            throw error;
        }
    }

  /**
   * Create a notification for any account type (user/host/coach) by
   * resolving which collection the user belongs to.
   */
  static async createNotificationForAnyAccount(
      userId: string,
      message: string,
      type: string = 'general',
      sessionId?: string,
      campId?: string,
      metadata?: any
  ) {
      try {
          const userResult = await DataOps.fetchUserFromAnyCollection(userId);
          const collection = userResult?.collection || 'user';

          const notificationId = `notification_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
          const notification: any = {
              id: notificationId,
              message,
              type,
              read: false,
              createdAt: new Date(),
              ...metadata
          };

          // Only add sessionId and campId if they are defined (Firestore doesn't allow undefined values)
          if (sessionId) notification.sessionId = sessionId;
          if (campId) notification.campId = campId;

          await DataOps.saveToSubcollection(collection, userId, 'notifications', notificationId, notification);
          
          return notification;
      } catch (error) {
          console.error(`Error creating notification for any account ${userId}:`, error);
          throw error;
      }
  }

    /**
     * Batch create notifications for multiple users (e.g., host and coach)
     * Efficiently writes to multiple user subcollections
     */
    static async createBatchNotifications(
        userIds: string[],
        message: string,
        type: string = 'general',
        sessionId?: string,
        campId?: string,
        metadata?: any
    ) {
        try {
            const promises = userIds.map(userId => 
                this.createNotification(userId, message, type, sessionId, campId, metadata)
            );
            await Promise.all(promises);
        } catch (error) {
            console.error('Error creating batch notifications:', error);
            throw error;
        }
    }

    /**
     * Listen to user's notifications in real-time
     * Only listens to their own subcollection - highly efficient
     */
    static listenToUserNotifications(
        userId: string, 
        onUpdate: (notifications: any[]) => void,
        limitCount: number = 50
    ) {
        return DataOps.listenToSubcollection(
            'user',
            userId,
            'notifications',
            {
                orderBy: { field: 'createdAt', direction: 'desc' },
                limit: limitCount
            },
            onUpdate
        );
    }

    /**
     * Listen to notifications for any account type (user/host/coach)
     * Automatically determines the correct collection based on account type
     */
    static listenToNotificationsForAnyAccount(
        userId: string,
        accountType: 'family' | 'host' | 'coach',
        onUpdate: (notifications: any[]) => void,
        limitCount: number = 50
    ) {
        const collection = DataOps.getCollectionNameForAccountType(accountType);
        return DataOps.listenToSubcollection(
            collection,
            userId,
            'notifications',
            {
                orderBy: { field: 'createdAt', direction: 'desc' },
                limit: limitCount
            },
            onUpdate
        );
    }

    /**
     * Mark notification as read (for family accounts only)
     */
    static async markAsRead(userId: string, notificationId: string) {
        try {
            await DataOps.updateSubcollectionDocument(
                'user',
                userId,
                'notifications',
                notificationId,
                { read: true }
            );
        } catch (error) {
            console.error('Error marking notification as read:', error);
            throw error;
        }
    }

    /**
     * Mark notification as read for any account type
     */
    static async markAsReadForAnyAccount(
        userId: string,
        notificationId: string,
        accountType: 'family' | 'host' | 'coach'
    ) {
        try {
            const collection = DataOps.getCollectionNameForAccountType(accountType);
            await DataOps.updateSubcollectionDocument(
                collection,
                userId,
                'notifications',
                notificationId,
                { read: true }
            );
        } catch (error) {
            console.error('Error marking notification as read:', error);
            throw error;
        }
    }

    /**
     * Delete a notification (for family accounts only)
     */
    static async deleteNotification(userId: string, notificationId: string) {
        try {
            await DataOps.deleteSubcollectionDocument(
                'user',
                userId,
                'notifications',
                notificationId
            );
        } catch (error) {
            console.error('Error deleting notification:', error);
            throw error;
        }
    }

    /**
     * Delete a notification for any account type
     */
    static async deleteNotificationForAnyAccount(
        userId: string,
        notificationId: string,
        accountType: 'family' | 'host' | 'coach'
    ) {
        try {
            const collection = DataOps.getCollectionNameForAccountType(accountType);
            await DataOps.deleteSubcollectionDocument(
                collection,
                userId,
                'notifications',
                notificationId
            );
        } catch (error) {
            console.error('Error deleting notification:', error);
            throw error;
        }
    }

    /**
     * Delete old notifications (cleanup job)
     */
    static async deleteOldNotifications(userId: string, daysOld: number = 30) {
        try {
            const cutoffDate = new Date();
            cutoffDate.setDate(cutoffDate.getDate() - daysOld);

            const oldNotifications = await DataOps.fetchFromSubcollection(
                'user',
                userId,
                'notifications',
                {
                    where: [{ field: 'createdAt', operator: '<', value: cutoffDate }]
                }
            );

            const deletePromises = oldNotifications.map(notif => 
                this.deleteNotification(userId, notif.id)
            );

            await Promise.all(deletePromises);
        } catch (error) {
            console.error('Error deleting old notifications:', error);
            throw error;
        }
    }

    /**
     * Notify about registration/unregistration
     * Sends to both host and assigned coach (if any)
     * Also sends email notifications if enabled
     */
    static async notifyRegistration(
        hostUserId: string,
        coachUserId: string | null,
        childNames: string[],
        parentName: string,
        sessionTitle: string,
        sessionId: string,
        campId: string,
        action: 'registered' | 'unregistered'
    ) {
        try {
            const childListText = childNames.length === 1 
                ? childNames[0]
                : childNames.length === 2
                ? `${childNames[0]} and ${childNames[1]}`
                : `${childNames.slice(0, -1).join(', ')}, and ${childNames[childNames.length - 1]}`;
            
            const message = `${parentName} has ${action} ${childListText} for "${sessionTitle}"`;
            
            const recipientIds = [hostUserId];
            if (coachUserId) recipientIds.push(coachUserId);

            // Send in-app notifications (support any account type: user/host/coach)
            await Promise.all(
                recipientIds.map((recipientId) => 
                    this.createNotificationForAnyAccount(
                        recipientId,
                        message,
                        'registration',
                        sessionId,
                        campId,
                        {
                            action,
                            childCount: childNames.length,
                            parentName
                        }
                    )
                )
            );

            // Send email notifications to each recipient
            const emailPromises = recipientIds.map(userId => 
                EmailService.sendRegistrationEmail(
                    userId,
                    childNames,
                    parentName,
                    sessionTitle,
                    action
                )
            );
            await Promise.all(emailPromises);
        } catch (error) {
            console.error('Error sending registration notifications:', error);
            throw error;
        }
    }

    /**
     * Send notification when coach adds comment for a student
     * Also sends email if enabled
     */
    static async notifyStudentComment(studentUserId: string, coachName: string, sessionTitle: string, date: string, sessionId: string, campId: string, targetUserId?: string, rawDate?: string, commentId?: string, childName?: string) {
        const message = `${coachName} added a comment for you in ${sessionTitle} on ${date}`;
        
        // Send in-app notification
        const notification = await this.createNotification(
            studentUserId, 
            message, 
            'coach_comment', 
            sessionId, 
            campId,
            {
                targetUserId, // The child the comment was for
                actionDate: rawDate || date,
                commentId,
                contentType: 'comment'
            }
        );

        // Send email notification if childName is provided
        if (childName) {
            await EmailService.sendCommentEmail(
                studentUserId,
                childName,
                coachName,
                sessionTitle,
                date
            );
        }

        return notification;
    }

    /**
     * Send notification when coach adds photo for a student
     * Also sends email if enabled
     */
    static async notifyStudentPhoto(studentUserId: string, coachName: string, sessionTitle: string, date: string, sessionId: string, campId: string, targetUserId?: string, rawDate?: string, photoId?: string, childName?: string) {
        const message = `${coachName} shared a moment for you in ${sessionTitle} on ${date}`;
        
        // Send in-app notification
        const notification = await this.createNotification(
            studentUserId, 
            message, 
            'coach_photo', 
            sessionId, 
            campId,
            {
                targetUserId, // The child the photo was for
                actionDate: rawDate || date,
                photoId,
                contentType: 'photo'
            }
        );

        // Send email notification if childName is provided
        if (childName) {
            await EmailService.sendPhotoEmail(
                studentUserId,
                childName,
                coachName,
                sessionTitle,
                date
            );
        }

        return notification;
    }

    /**
     * Send notification when coach adds multiple items for a student
     */
    static async notifyStudentUpdate(studentUserId: string, coachName: string, sessionTitle: string, date: string, items: string[]) {
        const itemText = items.length === 1 ? items[0] : 
                        items.length === 2 ? items.join(' and ') : 
                        `${items.slice(0, -1).join(', ')}, and ${items[items.length - 1]}`;
        
        const message = `${coachName} added ${itemText} for you in ${sessionTitle} on ${date}`;
        return await this.createNotification(studentUserId, message, 'coach_update');
    }

    /**
     * Send notification when a child is checked in
     * Also sends email if enabled
     */
    static async notifyAttendance(
        parentUserId: string,
        childName: string,
        sessionTitle: string,
        date: string,
        sessionId: string,
        campId: string,
        childId?: string,
        rawDate?: string
    ) {
        const message = `${childName} has been checked in for ${sessionTitle} on ${date}`;
        
        // Send in-app notification
        const notification = await this.createNotification(
            parentUserId,
            message,
            'attendance',
            sessionId,
            campId,
            {
                targetUserId: childId,
                actionDate: rawDate || date,
                contentType: 'attendance'
            }
        );

        // Send email notification
        await EmailService.sendAttendanceEmail(
            parentUserId,
            childName,
            sessionTitle,
            date
        );

        return notification;
    }

    /**
     * Notify users who have favorited a camp about a new session
     * Sends notification to all family accounts who have liked/favorited the camp
     * Also sends email notifications if enabled
     */
    static async notifyNewSessionToFavorites(
        campId: string,
        campName: string,
        sessionTitle: string,
        sessionId: string,
        sessionStartDate: string
    ) {
        try {
            // Fetch all users
            const allUsers = await DataOps.fetchAllDocumentsFromFirebase('user');
            
            if (!allUsers || allUsers.length === 0) {
                console.error('No users found in database');
                return;
            }
            
            // Filter to find family accounts who have favorited this camp
            const favoriteUsers = allUsers.filter((user: any) => 
                (user.accountType === 'family' || user.accountType === 'user') && 
                user.likedCamps && 
                Array.isArray(user.likedCamps) && 
                user.likedCamps.includes(campId)
            );
            
            if (favoriteUsers.length === 0) {
                return; // No users have favorited this camp
            }
            
            // Format the date for the message
            const formattedDate = this.formatNotificationDate(sessionStartDate);
            const message = `${campName} published a new session: "${sessionTitle}" starting ${formattedDate}`;
            
            // Send in-app notifications to all users who favorited this camp
            const userIds = favoriteUsers.map((user: any) => user.userId);
            await this.createBatchNotifications(
                userIds,
                message,
                'new_session',
                sessionId,
                campId,
                {
                    sessionTitle,
                    campName,
                    startDate: sessionStartDate
                }
            );

            // Send email notifications to each user
            const emailPromises = userIds.map(userId => 
                EmailService.sendNewSessionEmail(
                    userId,
                    campName,
                    sessionTitle,
                    formattedDate
                )
            );
            await Promise.all(emailPromises);
            
        } catch (error) {
            console.error('Error sending new session notifications:', error);
            throw error;
        }
    }

    /**
     * Format date for notification display
     */
    static formatNotificationDate(date: string): string {
        try {
            return new Date(date).toLocaleDateString('en-US', { 
                weekday: 'long', 
                month: 'short', 
                day: 'numeric' 
            });
        } catch (error) {
            return date;
        }
    }
}

export default NotificationOps; 